// Page saves made without a connection, kept on this device until they can be replayed. One entry per page: the
// latest local version plus the server version it started from, so replay can merge (utils/offlineMerge).

import { type UseStore, createStore, del, entries, get, set } from 'idb-keyval'
import type { drawingPage } from 'reduxStore/projects/types'

export interface QueuedSave {
  pageId: string
  projectId: string
  userId: string
  base: drawingPage
  local: drawingPage
  queuedAt: number
}

export const OUTBOX_CHANGED = 'takecost:outbox-changed'

let store: UseStore | null = null
const outbox = () => {
  store ??= createStore('takecost-offline', 'page-saves')
  return store
}

const changed = () => window.dispatchEvent(new Event(OUTBOX_CHANGED))

export const getQueuedSave = (pageId: string) => get<QueuedSave>(pageId, outbox())

/** Keeps the first base, so every later offline edit to the page still diffs against what the server last had. */
export async function addToOutbox(page: drawingPage, serverCopy: drawingPage, userId: string): Promise<QueuedSave> {
  const pageId = page.id as string
  const existing = await getQueuedSave(pageId)
  const entry: QueuedSave = {
    pageId,
    projectId: page.projectId as string,
    userId,
    base: existing?.base ?? serverCopy,
    local: page,
    queuedAt: Math.max(Date.now(), (existing?.queuedAt ?? 0) + 1),
  }
  await set(pageId, entry, outbox())
  changed()
  return entry
}

export async function queuedSaves(userId: string): Promise<QueuedSave[]> {
  const all = await entries<string, QueuedSave>(outbox())
  return all
    .map(([, entry]) => entry)
    .filter((entry) => entry.userId === userId)
    .sort((a, b) => a.queuedAt - b.queuedAt)
}

/** Removes the entry only if no newer offline edit replaced it while it was being replayed. */
export async function removeFromOutbox(entry: QueuedSave): Promise<void> {
  const current = await getQueuedSave(entry.pageId)
  if (current?.queuedAt !== entry.queuedAt) return
  await del(entry.pageId, outbox())
  changed()
}

/** A request that failed because the device couldn't reach the server, not because the server refused it. */
export const isNetworkError = (error: unknown): boolean => {
  const e = error as { response?: unknown; code?: string; message?: string } | null
  return !!e && !e.response && (e.code === 'ERR_NETWORK' || e.message === 'Network Error' || !navigator.onLine)
}
